import { useId, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  useScrollLock,
  useWindowEscapeFallback
} from '@/components/lw/hooks';
import { LW_DS_CLASS } from '@/components/lw/LwThemeScope';

interface LoDialogShellProps {
  children: ReactNode;
  onClose: () => void;
  cardClassName?: string;
  initialFocusRef?: RefObject<HTMLElement | null>;
}

function useReturnFocus() {
  const returnFocusRef = useRef<HTMLElement | null>(
    typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
  );

  useLayoutEffect(() => {
    const returnTarget = returnFocusRef.current;
    return () => {
      if (returnTarget?.isConnected) {
        returnTarget.focus();
      }
    };
  }, []);

  return returnFocusRef;
}

/**
 * Radix-backed parity shell for the LO pipeline's legacy modal grammar.
 *
 * The separate tinted scrim + transparent `.modal` click target are
 * intentional: the characterized LO contract dismisses on click, not on the
 * pointer-down event Radix uses by default. Radix still owns modality, Escape,
 * focus containment/return, and stacked body locking.
 */
export function LoDialogShell({
  children,
  onClose,
  cardClassName = '',
  initialFocusRef
}: LoDialogShellProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useReturnFocus();
  const contentId = `lo-dialog-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`;
  const classes = cardClassName ? `modal-card ${cardClassName}` : 'modal-card';

  useScrollLock();
  useWindowEscapeFallback(contentRef, onClose);

  return (
    <DialogPrimitive.Root
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <div className={LW_DS_CLASS}>
          <DialogPrimitive.Overlay asChild>
            <div className="scrim" onClick={onClose} />
          </DialogPrimitive.Overlay>
          <div className="modal" onClick={onClose}>
            <DialogPrimitive.Content
              ref={contentRef}
              asChild
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                (initialFocusRef?.current ?? contentRef.current)?.focus();
              }}
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                returnFocusRef.current?.focus();
              }}
              onPointerDownOutside={(event) => event.preventDefault()}
            >
              <div
                id={contentId}
                className={classes}
                aria-modal="true"
                data-lw-overlay-layer=""
                tabIndex={-1}
                onClick={(event) => event.stopPropagation()}
              >
                {children}
              </div>
            </DialogPrimitive.Content>
          </div>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function LoDialogTitle({ children }: { children: ReactNode }) {
  return (
    <DialogPrimitive.Title asChild>
      <div className="modal-title">{children}</div>
    </DialogPrimitive.Title>
  );
}

export function LoDialogDescription({ children }: { children: ReactNode }) {
  return (
    <DialogPrimitive.Description asChild>
      <div className="modal-sub">{children}</div>
    </DialogPrimitive.Description>
  );
}

/** Radix Dialog rendered with the existing right-drawer DOM and visual classes. */
export function LoDrawerShell({
  children,
  label,
  onClose
}: {
  children: ReactNode;
  label: string;
  onClose: () => void;
}) {
  const contentRef = useRef<HTMLElement>(null);
  const returnFocusRef = useReturnFocus();
  const contentId = `lo-drawer-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`;

  useScrollLock();
  useWindowEscapeFallback(contentRef, onClose);

  return (
    <DialogPrimitive.Root
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <div className={LW_DS_CLASS}>
          <DialogPrimitive.Overlay asChild>
            <div className="scrim" onClick={onClose} />
          </DialogPrimitive.Overlay>
          <DialogPrimitive.Content
            asChild
            aria-describedby={undefined}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              contentRef.current?.focus();
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              returnFocusRef.current?.focus();
            }}
            onPointerDownOutside={(event) => event.preventDefault()}
          >
            <aside
              ref={contentRef}
              id={contentId}
              className="drawer"
              aria-label={label}
              aria-modal="true"
              data-lw-overlay-layer=""
              tabIndex={-1}
            >
              <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>
              {children}
            </aside>
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
